import axios from "axios";
import { baseURL } from "../Constants";
import { Match } from "../models/Match";
import { MapStats } from "../models/MapStats";
import { UpcomingMatch } from "../models/UpcomingMatch";
import { TeamPrediction } from "../models/TeamPrediction";
import { StandingsPrediction } from "../models/StandingsPrediction";
import { Standing } from "../models/Standing";
import { VetoHistoryEntry } from "../models/VetoHistory";

const url = `${baseURL}/Match`;

/**
 * Saves the signed-in user's prediction for one division. The backend upserts on either route and
 * checks the token; `hasExisting` only picks the route an older backend expects (create inserts,
 * edit updates), so this works across a deploy in either order.
 */
export const saveStandingsPrediction = async (
    token: string,
    prediction: { steamId: string; season: number; division: string; teams: TeamPrediction[] },
    hasExisting: boolean
): Promise<void> => {
    await axios.post(
        `${baseURL}/StandingsPrediction/${hasExisting ? "edit" : "create"}`,
        prediction,
        { headers: { Authorization: `Bearer ${token}` } }
    );
};

/** A user's predictions for a season. Throws on failure, unlike getAllUserPredictions: the predict
 *  page must not offer a blank board over a prediction it failed to load, or a save would overwrite it. */
export const getUserPredictionsStrict = async (steamId: string, season: number): Promise<StandingsPrediction[]> => {
    const response = await axios.get(`${baseURL}/StandingsPrediction/getByUser`, { params: { steamId, season } });
    return Array.isArray(response.data) ? response.data : [];
};

export interface PredictionLeaderboardRow {
    steamId: string;
    playerName: string;
    averageScore: number;
    totalScore: number;
    predictionsCount: number;
    /** Divisions that were scored. */
    divisions: string[];
    /** Every division the user predicted, scored or not (missing on an older backend). */
    predictedDivisions?: string[];
    /** Teams placed on their exact current rank (missing on an older backend). */
    exactHits?: number;
}

export const getPredictionLeaderboard = async (season: number, division?: string): Promise<PredictionLeaderboardRow[]> => {
    const response = await axios.get(`${baseURL}/StandingsPrediction/leaderboard`, {
        params: division ? { season, division } : { season },
    });
    return Array.isArray(response.data) ? response.data : [];
};

// In src/services/MatchService.ts
export const fetchMatchesBetweenPlayers = async (player1Id: string, player2Id: string): Promise<any> => {
    try {
      const response = await fetch(`${url}/betweenPlayers?player1SteamId=${player1Id}&player2SteamId=${player2Id}`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch matches between players');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error fetching matches between players:', error);
      throw error;
    }
  };

export const fetchHeadToHeadMatches = async (player1Id: string, player2Id: string) => {
    try {
      const response = await fetch(`${url}/headToHead?player1Id=${player1Id}&player2Id=${player2Id}`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch head-to-head matches');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error fetching head-to-head matches:', error);
      throw error;
    }
  };

export const getHeadToHeadMatches = async (player1Id: string, player2Id: string) => {
    try {
      const response = await axios.get(`${url}/headToHead?player1Id=${player1Id}&player2Id=${player2Id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching head-to-head data:', error);
      throw error;
    }
  };

export async function hasPrediction(steamId: string , season: number): Promise<boolean> {
    try {
      const response = await axios.get(
        `${baseURL}/StandingsPrediction/hasPrediction?steamId=${steamId}&season=${season}`
      );
      return response.data; 
      // Expecting a boolean response from the server
    } catch (error) {
      console.error("Error checking for prediction:", error);
      throw error; 
    }
  }

export const arePredictionsLocked = async (season: number): Promise<boolean> => {
    try {
        const response = await axios.get(
            `${baseURL}/StandingsPrediction/isLocked?season=${season}`
        );
        return response.data;
    } catch (error) {
        console.error('Error checking if predictions are locked:', error);
        return false;
    }
}

export interface PredictionLockStatus {
    season: number;
    locked: boolean;
    lockTime: string;
    serverTime: string;
}

/** The current season's prediction deadline, same shape as Fantasy/getFantasyLockStatus. Throws on failure. */
export const getPredictionLockStatus = async (): Promise<PredictionLockStatus> => {
    const response = await axios.get(`${baseURL}/StandingsPrediction/lockStatus`);
    return response.data;
};

export const getAllUserPredictions = async (
    steamId: string,
    season?: number,
    division?: string
 ): Promise<StandingsPrediction[]> => {
    try {
        const params = new URLSearchParams();
        params.append('steamId', steamId);
        if (season) params.append('season', season.toString());
        if (division) params.append('division', division);
 
        const response = await axios.get(`${baseURL}/StandingsPrediction/getByUser?${params}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching user predictions:', error);
        return [];
    }
 };

export const getAllStandingsPredictions = async (
    season?: number,
    division?: string
): Promise<StandingsPrediction[]> => {
    try {
        let url = `${baseURL}/StandingsPrediction/getAll`;
        const params = new URLSearchParams();
        if (season) params.append('season', season.toString());
        if (division) params.append('division', division);
        
        if (params.toString()) {
            url += `?${params.toString()}`;
        }
        
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const getUpcomingMatchById = async (id: string): Promise<UpcomingMatch> => {
    try {
        const response = await axios.get(`${url}/getUpcomingMatchById/${id}`);
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const getLatestMatches = async (page: number): Promise<Match[]> => {
    try {
        const response = await axios.get(`${url}/latest?page=${page}`);
        return response.data;
    }
    catch (error) {
        throw error;
    }
}

export const getLatestSeason = async (): Promise<number> => {
    try {
        const response = await axios.get(`${url}/latestSeason`);
        return response.data;
    }
    catch (error) {
        throw error;
    }
}

export const getUpcomingMatches = async (): Promise<UpcomingMatch[]> => {
    try {
        const response = await axios.get(`${url}/getUpcomingMatches`);
        return response.data;
    }
    catch (error) {
        throw error;
    }
}

export const getUpcomingMatchesForTeam = async (teamId: string): Promise<UpcomingMatch[]> => {
    try {
        const response = await axios.get(`${url}/getUpcomingMatchesForTeam?teamId=${teamId}`);
        return response.data;
    }
    catch (error) {
        throw error;
    }
}

export const getOldestSeason = async (): Promise<number> => {
    try {
        const response = await axios.get(`${url}/oldestSeason`);
        return response.data;
    }
    catch (error) {
        throw error;
    }
}

export const getMatchesForTeamBySeason = async (teamId: string, season: number): Promise<Match[]> => {
    try {
        const response = await axios.get(`${url}/team`, { params: { teamId, season } });
        return response.data;
    }
    catch (error) {
        throw error;
    }
}

/** Parsed past vetoes by the team and by earlier lineups holding two or more of `playerIds`. */
export const getVetoHistory = async (teamId: string, playerIds: string[]): Promise<VetoHistoryEntry[]> => {
    const response = await axios.get(`${url}/vetoHistory`, { params: { teamId, playerIds: playerIds.join(',') } });
    return response.data;
};

export const getVetoStatsForTeamBySeason = async (teamId: string, season: string): Promise<MapStats[]> => {
    try {
        const response = await axios.get(`${url}/getVetoStats`, { params: { teamid: teamId, season } });
        return response.data.map((item: { mapName: string; amountPicked: number; amountBanned: number; amountPlayed: number; amountWon: number; chosenCTPercentage: number; }) => new MapStats(
            item.mapName,
            item.amountPicked,
            item.amountBanned,
            item.amountPlayed,
            item.amountWon,
            item.chosenCTPercentage,
        ));
    }
    catch (error) {
        throw error;
    }
}

export const getMatchesForPlayerBySeason = async (steamId: string, season: number): Promise<Match[]> => {
    try {
        const response = await axios.get(`${url}/player`, { params: { steamId, season } });
        return response.data;
    }
    catch (error) {
        throw error;
    }
}

export const getRecentMatchesWithStats = async (steamId: string, season: number, limit: number = 10, scope?: 'liga' | 'kval'): Promise<any[]> => {
    try {
        const response = await axios.get(`${url}/player/withStats`, {
            params: { steamId, season, limit, scope }
        });
        return response.data;
    }
    catch (error) {
        throw error;
    }
}

export const getAllMatchesWithStats = async (steamId: string, limit: number = 500): Promise<any[]> => {
    try {
        const response = await axios.get(`${url}/player/withStats/all`, {
            params: { steamId, limit }
        });
        return response.data;
    }
    catch (error) {
        throw error;
    }
}

export const getMatchById = async (matchId: string): Promise<Match[]> => {
    try {
        const response = await axios.get(`${url}/${matchId}`);
        return response.data;
    }
    catch (error) {
        throw error;
    }
}

export const getTeamStanding = async (
    teamId: string,
    season: number
  ): Promise<Standing> => {
    try {
      const response = await axios.get(
        `${url}/standingForTeam?teamId=${teamId}&season=${season}`
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  };

export const getDivisionStandingsAll = async (season: number) => {
    try {
        const response = await axios.get(`${url}/getAllDivisionStandings?season=${season}`);
        return response.data;
    }
    catch (error) {
        throw error;
    }
}

export const getDivisionStandings = async (season: number, division: string) => {
    try {
        const response = await axios.get(`${url}/getAllDivisionStandings?season=${season}&majorDivision=${division}`);
        return response.data;
    }
    catch (error) {
        throw error;
    }
}

export const getDivisionsBySeason = async (season: number): Promise<string[]> => {
    try {
        const response = await axios.get(`${url}/getDivisionsBySeason?season=${season}`);
        return response.data;
    }
    catch (error) {
        throw error;
    }
}

export const getPlayersAvgRating = async (matchId: string): Promise<{ [steamId: string]: number }> => {
    try {
        const response = await axios.get(`${url}/getPlayersAvgRating?matchId=${matchId}`);
        return response.data;
    }
    catch (error) {
        throw error;
    }
}
